import {
  type FramePathReport,
  type FramePathSummary,
} from "../modules/teleop-session/media/video-frame-path";
import styles from "./VideoLatencyPanel.module.scss";

const format = (n: number | null, unit = "") => (n === null ? "—" : `${n.toFixed(1)}${unit}`);

export function VideoFramePathPanel({
  camera,
  report,
  exportReport,
  start,
  stop,
  supported,
}: {
  camera: string;
  report: FramePathSummary | null;
  exportReport: () => FramePathReport | null;
  start: () => void;
  stop: () => void;
  supported: boolean;
}) {
  const running = report?.status === "running" || report?.status === "finishing";
  function download() {
    const fullReport = exportReport();
    if (!fullReport) return;
    const blob = new Blob(
      [
        JSON.stringify(
          {
            camera,
            boundary: "backend SRTP send entry to estimated compositor display",
            report: fullReport,
          },
          null,
          2
        ),
      ],
      { type: "application/json" }
    );
    const url = URL.createObjectURL(blob),
      anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `frame-path-${camera.replace(/[^\p{L}\p{N}]+/gu, "-")}-${fullReport.startedAtIso.replace(/[:.]/g, "-")}.json`;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  const fields = [
    ["Backend → показ", "backendToDisplayMs"],
    ["Доставка WebRTC", "transitMs"],
    ["Получение → показ", "receiveToDisplayMs"],
    ["Отправка пакетов кадра", "sendMs"],
    ["Декодирование кадра", "decodeMs"],
  ] as const;
  const uncertainty = report?.clockUncertaintyMs ?? null;
  return (
    <section className={styles.panel} aria-label={`${camera}: путь кадра`}>
      <div className={styles.actions}>
        {running ? (
          <button type="button" onClick={stop}>
            Остановить замер кадров
          </button>
        ) : (
          <button type="button" disabled={!supported} onClick={start}>
            Измерить путь кадра за 60 с
          </button>
        )}
        {report && !running && (
          <button type="button" onClick={download} aria-label={`${camera}: скачать замер кадров`}>
            Скачать замер кадров
          </button>
        )}
      </div>
      {report && (
        <>
          <p className={styles.progress} role="status">
            {report.status === "running"
              ? `Замер кадров · осталось ${report.remainingSeconds} с`
              : report.status === "finishing"
                ? "Сопоставление последних кадров…"
                : report.status === "complete"
                  ? "Замер кадров завершён"
                  : `Замер остановлен: ${report.reason ?? "по запросу"}`}
          </p>
          <table aria-label={`${camera}: задержка кадров`}>
            <thead>
              <tr>
                <th>Участок</th>
                <th>Среднее</th>
                <th>P95</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(([label, key]) => {
                const d = report.distributions[key];
                return (
                  <tr key={key}>
                    <th scope="row">{label}</th>
                    <td>{format(d.mean, " мс")}</td>
                    <td>{format(d.p95, " мс")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className={styles.note}>
            FPS: декодирование {format(report.decodedFps)} · передано на показ{" "}
            {format(report.presentedFps)} · наблюдения {format(report.callbackFps)}.
          </p>
          <p className={styles.note}>
            Сопоставлено {report.matchedFrames}, без метки отправки/сверки {report.unmatchedFrames}.
            Видимое время {format(report.measuredSeconds, " с")}, скрыто{" "}
            {format(report.hiddenSeconds, " с")}. Сверка часов ±{format(uncertainty, " мс")}.
          </p>
          <p className={styles.note}>
            Максимальный интервал наблюдений {format(report.maxCallbackGapMs, " мс")}; между
            вызовами браузер передал ещё {report.missedCallbacks} кадров на показ.
          </p>
        </>
      )}
      <p className={styles.note}>
        {!supported ? "Браузер не поддерживает покадровые метки. " : ""}Первые 5 с исключены.
        Держите видео на видимой вкладке. «Показ» — ожидаемое время отображения браузером;
        физическая задержка экрана сюда не входит. Несопоставленные кадры не считаются потерянными.
      </p>
    </section>
  );
}
